import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { Button } from "../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../components/ui-kit/dialog";
import { DateInput } from "../../components/ui-kit/fields/date-input";
import { Form } from "../../components/ui-kit/form/form";
import { FormModel } from "../../components/ui-kit/form/form-model";
import { FormRow } from "../../components/ui-kit/form/form-row";
import { getServerTodayISO } from "../../utils/server-time";
import { assert, dateTimeOrNull } from "../../utils/utils";
import { Patient, PatientData } from "./patient";

export const MonitoringMuteFormRow = observer(
  function MonitoringMuteFormRow(props: { patient: Patient; form: FormModel }) {
    assert(props.patient.data, "Patient data is missing");
    const { t } = useLingui();

    const [dialogOpen, setDialogOpen] = useState(false);

    return (
      <>
        <FormRow
          label={t`Monitoring mute`}
          data={
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-0">{mutingString(props.patient.data)}</div>

              {props.patient.data.monitoring_muted && (
                <Button
                  size="sm"
                  onClick={() =>
                    props.patient.updatePatientField(
                      {
                        monitoring_muted: false,
                        monitoring_mute_start_date: null,
                        monitoring_mute_end_date: null,
                      },
                      false,
                    )
                  }
                >
                  <Trans>Unmute</Trans>
                </Button>
              )}

              {!props.patient.data.monitoring_muted &&
                !props.patient.data.monitoring_mute_start_date && (
                  <Button
                    size="sm"
                    onClick={() =>
                      props.patient.updatePatientField(
                        {
                          monitoring_muted: true,
                          monitoring_mute_start_date: null,
                          monitoring_mute_end_date: null,
                        },
                        false,
                      )
                    }
                  >
                    <Trans>Mute</Trans>
                  </Button>
                )}

              {!props.patient.data.monitoring_muted &&
                !props.patient.data.monitoring_mute_start_date && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setDialogOpen(true);
                    }}
                  >
                    <Trans>Schedule mute</Trans>
                  </Button>
                )}

              {!props.patient.data.monitoring_muted &&
                props.patient.data.monitoring_mute_start_date && (
                  <Button
                    size="sm"
                    onClick={() =>
                      props.patient.updatePatientField(
                        {
                          monitoring_mute_start_date: null,
                          monitoring_mute_end_date: null,
                        },
                        false,
                      )
                    }
                  >
                    <Trans>Unschedule mute</Trans>
                  </Button>
                )}
            </div>
          }
        />

        {dialogOpen && (
          <ScheduleMuteDialog
            patient={props.patient}
            onClose={() => setDialogOpen(false)}
          />
        )}
      </>
    );
  },
);

const ScheduleMuteDialog = observer(function ScheduleMuteDialog(props: {
  patient: Patient;
  onClose: () => void;
}) {
  const [body] = useState(() =>
    observable({
      monitoring_muted: false,
      monitoring_mute_start_date: null as string | null,
      monitoring_mute_end_date: null as string | null,
    }),
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
    >
      <DialogHeader>
        <Trans>Schedule mute</Trans>
      </DialogHeader>
      <Form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={async () => {
          if (body.monitoring_mute_start_date === getServerTodayISO()) {
            body.monitoring_muted = true;
            body.monitoring_mute_start_date = null;
          }
          await props.patient.updatePatientField(body, false);
          props.onClose();
        }}
      >
        <DialogBody>
          <DateInput
            model={body}
            type="date"
            attr="monitoring_mute_start_date"
          />
          <DateInput model={body} type="date" attr="monitoring_mute_end_date" />
        </DialogBody>

        <DialogFooter>
          <Button onClick={props.onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit">
            <Trans>Confirm</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});

function mutingString(data: PatientData) {
  if (data.monitoring_muted) {
    return data.monitoring_mute_end_date
      ? i18n._(
          msg`muted until ${dateTimeOrNull(data.monitoring_mute_end_date, false) ?? ""}`,
        )
      : i18n._(msg`muted`);
  } else if (data.monitoring_mute_start_date) {
    return data.monitoring_mute_end_date
      ? i18n._(
          msg`mute scheduled from ${dateTimeOrNull(data.monitoring_mute_start_date, false) ?? ""} until ${dateTimeOrNull(data.monitoring_mute_end_date, false) ?? ""}`,
        )
      : i18n._(
          msg`mute scheduled from ${dateTimeOrNull(data.monitoring_mute_start_date, false) ?? ""}`,
        );
  } else {
    return i18n._(msg`not muted`);
  }
}
